import {
    ElevationRideTypes,
    PaceProfileFactors,
    AverageSpeedByRideType,
    AscentTimeSecondsPerMeter,
    DefaultElevationSmoothingSpan,
    ElevationSmoothingSpanByRideType,
    ElevationSmoothingWindowFactor
} from '@js/components/modules/constants';
import I18n from '@js/modules/translations';

class Computation {
    UNIT_CONVERSION = {
        KM_PER_MILE: 1.609344,
        M_PER_FEET: 0.3048,
        PI: 3.1415926,
        DEG_TO_RAD: 0.0174532925199433,
        EARTH_RADIUS: 6371.0088
    };

    /* Public Methods
     ******************** */
    formatDistance = (distance, {
        unit = window.unit,
        smartUnit = false,
        kUnit = true,
        decimalLimit = 3
    } = {}) => {
        const loc = (v) => I18n.locale === 'en' ? String(v) : String(v).replace('.', ',');

        if (!distance) {
            const unitKey = kUnit && !smartUnit ? 'distance_unit_k' : 'distance_unit';
            return `0${I18n.t(`js.metrics.${unit}.${unitKey}`)}`;
        }

        if (smartUnit) {
            if (this.meterToUnit(distance, {unit}) > (unit === 'mi' ? this.UNIT_CONVERSION.KM_PER_MILE * 1000 : 1000)) {
                return `${loc(this.kmToUnit(distance / (unit === 'mi' && !kUnit ? 1 : 1_000), {precision: decimalLimit, unit}))}${I18n.t(`js.metrics.${unit}.distance_unit_k`)}`;
            } else {
                return `${loc(this.meterToUnit(distance, {precision: 0, unit}))}${I18n.t(`js.metrics.${unit}.distance_unit`)}`;
            }
        } else if (!kUnit) {
            return `${loc(this.meterToUnit(distance, {precision: decimalLimit, unit}))}${I18n.t(`js.metrics.${unit}.distance_unit`)}`;
        } else {
            return `${loc(this.kmToUnit(distance, {precision: decimalLimit, unit}))}${I18n.t(`js.metrics.${unit}.distance_unit_k`)}`;
        }
    };

    // formatMeterDistance = (distance, {unit = window.unit} = {}) => {
    //     return `${this.meterToUnit(distance)}${I18n.t(`js.metrics.${unit}.distance_unit`)}`;
    // };


    formatDuration = (time, {
        withSeconds = true,
        maxTimeForSeconds = 600, // Don't display seconds if above 10 minutes
        formatted = true,
        withLeadingZero = true,
        chronoFormat = false
    } = {}) => {
        let displaySeconds = withSeconds;
        if (time && maxTimeForSeconds && time > maxTimeForSeconds) {
            displaySeconds = false;
        }

        if (!time) {
            return undefined;
        } else if (typeof time === 'string' && time.includes(':')) {
            const [hours, minutes, seconds] = time.split(':');
            if (formatted) {
                return this._displayFormattedTime(Math.floor(parseInt(hours, 10)), Math.floor(parseInt(minutes, 10)), displaySeconds ? Math.floor(parseInt(seconds, 10)) : undefined, withLeadingZero);
            } else {
                return (parseInt(hours) || 0) * 3600 + (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
            }
        } else {
            const [hours, minutes, seconds] = this._secondsToHMS(time);

            if (chronoFormat) {
                return this._formatToChrono(hours, minutes, seconds);
            } else {
                return this._displayFormattedTime(hours, minutes, displaySeconds ? seconds : undefined, withLeadingZero);
            }
        }
    };

    isDurationEqual = (timeA, timeB) => {
        if (!timeA || !timeB) {
            return false;
        }

        let formattedTimeA;
        if (typeof timeA === 'string') {
            if (timeA.includes(':')) {
                const [hoursA, minutesA, secondsA] = timeA.split(':');
                formattedTimeA = parseInt(hoursA, 10) + parseInt(minutesA, 10) + parseInt(secondsA, 10);
            } else {
                formattedTimeA = parseInt(timeA, 10);
            }
        } else {
            formattedTimeA = timeA;
        }

        let formattedTimeB;
        if (typeof timeB === 'string' && timeB.includes(':')) {
            if (timeB.includes(':')) {
                const [hoursB, minutesB, secondsB] = timeB.split(':');
                formattedTimeB = parseInt(hoursB, 10) * 3600 + parseInt(minutesB, 10) * 60 + parseInt(secondsB, 10);
            } else {
                formattedTimeB = parseInt(timeB, 10);
            }
        } else {
            formattedTimeB = timeB;
        }

        return formattedTimeA === formattedTimeB;
    };

    formatPace = (speed, {unit = window.unit} = {}) => {
        if (typeof speed === 'string') {
            speed = parseFloat(speed?.replace(',', '.'));
        }

        if (!speed || speed <= 0) return undefined;

        const paceInMinutes = 60 / speed;

        if (paceInMinutes > 60) return undefined;

        let minutes = Math.floor(paceInMinutes);
        let seconds = Math.round((paceInMinutes - minutes) * 60);
        if (seconds === 60) {
            minutes += 1;
            seconds = 0;
        }

        const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
        return `${minutes}:${formattedSeconds}${I18n.t(`js.metrics.${unit}.pace_unit`)}`;
    };

    // Converts a pace string "M:SS" or "M" to speed in the user's unit
    parsePaceToSpeed = (paceInput) => {
        if (!paceInput) return 0;

        const paceString = String(paceInput).replace(',', '.').trim();
        let paceInMinutes;

        if (paceString.includes(':')) {
            const [minutes, seconds] = paceString.split(':');
            paceInMinutes = (parseInt(minutes) || 0) + (parseInt(seconds) || 0) / 60;
        } else {
            paceInMinutes = parseFloat(paceString);
        }

        if (!paceInMinutes || paceInMinutes <= 0) return 0;

        return parseFloat((60 / paceInMinutes).toFixed(2));
    };

    formatSpeed = (speed, {
        unitTarget = false,
        unit = window.unit
    } = {}) => {
        if (!speed) return undefined;
        const value = this.speedToUnit(speed, {unitTarget, unit});
        const localizedValue = I18n.locale === 'en' ? value : String(value).replace('.', ',');
        return `${localizedValue}${I18n.t(`js.metrics.${unit}.speed_unit`)}`;
    };

    computeDuration = (meterDistance, speed, {
        unit = window.unit,
        chronoDuration = false,
        formatted = true
    } = {}) => {
        if (unit === 'mi') {
            meterDistance /= this.UNIT_CONVERSION.KM_PER_MILE;
        }

        const durationInSeconds = (parseFloat(meterDistance) / parseFloat(speed)) * 3.6;

        if (formatted) {
            const [hours, minutes, seconds] = this._secondsToHMS(durationInSeconds);

            return chronoDuration ? this._formatToChrono(hours, minutes, seconds) : this._displayFormattedTime(hours, minutes, seconds);
        } else {
            return durationInSeconds;
        }
    };

    computeSpeed = (distance, duration, {formatted = true} = {}) => {
        if (!distance || !duration) {
            return 0;
        }

        let durationInSeconds;
        if (typeof duration === 'string' && duration.includes(':')) {
            const durations = duration.split(':');

            durationInSeconds = (parseInt(durations[2]) || 0) + (parseInt(durations[1]) || 0) * 60 + (parseInt(durations[0]) || 0) * 3600;
        } else {
            durationInSeconds = duration;
        }

        const speed = durationInSeconds ? (parseFloat(distance) / 1000) / (durationInSeconds / 3600) : 0;

        return formatted ? this.formatSpeed(speed) : speed;
    };

    kmToUnit = (distance, {
        precision = 0,
        unit = window.unit
    } = {}) => {
        if (typeof distance === 'string') {
            distance = parseFloat(distance?.replace(',', '.'));
        }

        if (unit === 'mi') {
            distance /= this.UNIT_CONVERSION.KM_PER_MILE * 1000;
            // Keep tenths of a mile even on long routes (was rounded to whole miles).
            return (distance > 10 ? distance.toFixed(1) : distance.toFixed(precision || 3)).toString();
        } else {
            return precision ? distance.toFixed(precision) : Math.round(distance * 1000);
        }
    };

    meterToUnit = (distance, {
        precision,
        unit = window.unit
    } = {}) => {
        if (typeof distance === 'string') {
            distance = parseFloat(distance?.replace(',', '.'));
        }

        if (unit === 'mi') {
            distance /= this.UNIT_CONVERSION.M_PER_FEET;
            return (distance > 10 ? Math.round(distance) : parseFloat(distance.toFixed(precision ?? 3))).toString();
        } else {
            return precision ? parseFloat(distance.toFixed(precision)) : Math.round(distance);
        }
    };

    speedToUnit = (speed, {
        unitTarget = null,
        unit = window.unit
    }) => {
        if (typeof speed === 'string') {
            speed = parseFloat(speed?.replace(',', '.'));
        }

        if (unitTarget === 'kmh') {
            speed *= this.UNIT_CONVERSION.KM_PER_MILE;
        } else if (unitTarget === 'mph') {
            // Do nothing
        } else if (unit === 'mi') {
            speed /= this.UNIT_CONVERSION.KM_PER_MILE;
        }

        return parseFloat(speed.toFixed(2));
    };

    combineChronoDuration = (firstChrono, secondChrono) => {
        if (!firstChrono || !secondChrono) {
            return firstChrono || secondChrono;
        }

        const [firstHours, firstMinutes, firstSeconds] = firstChrono.split(':').map((time) => parseInt(time));
        const [secondHours, secondMinutes, secondSeconds] = secondChrono.split(':').map((time) => parseInt(time));

        let newHours = firstHours + secondHours;
        let newMinutes = firstMinutes + secondMinutes;
        let newSeconds = firstSeconds + secondSeconds;

        if (newSeconds > 59) {
            newMinutes += 1;
            newSeconds %= 60;
        }
        if (newMinutes > 59) {
            newHours += 1;
            newMinutes %= 60;
        }

        return this._formatToChrono(newHours, newMinutes, newSeconds);
    };

    formatHeartRate = (heartRate) => {
        return `${heartRate}${I18n.t('js.metrics.heart_rate')}`;
    };

    computeElevationApsis = (elevations) => {
        let maxElevation = elevations[0];
        let minElevation = elevations[0];
        let positiveElevation = 0;
        let negativeElevation = 0;

        for (let i = 0; i < elevations.length; i++) {
            const elevation = elevations[i];
            if (i === 0) continue;

            if (elevation > maxElevation) {
                maxElevation = elevation;
            }

            if (elevation < minElevation && elevation !== null && elevation !== undefined) {
                minElevation = elevation;
            }

            if (
                elevations[i] !== null && elevations[i] !== undefined &&
                elevations[i - 1] !== null && elevations[i - 1] !== undefined
            ) {
                if (elevations[i] > elevations[i - 1]) {
                    positiveElevation += elevations[i] - elevations[i - 1];
                } else {
                    negativeElevation += elevations[i - 1] - elevations[i];
                }
            }
        }

        return {
            positiveElevation: Math.round(positiveElevation),
            negativeElevation: Math.round(negativeElevation),
            minimumAltitude: Math.round(minElevation),
            maximumAltitude: Math.round(maxElevation)
        };
    };

    // Detect categorized climbs from an elevation profile.
    // coordinateElevations: array of { deltaDistance (m), elevation (m) }.
    // Returns [{ startDistance, endDistance, length, gain, avgGrade, maxGrade, category }] (m / %).
    detectClimbs = (coordinateElevations) => {
        if (!Array.isArray(coordinateElevations) || coordinateElevations.length < 2) {
            return [];
        }

        // Cumulative distance + elevation, skipping points without elevation.
        const pts = [];
        let dist = 0;
        coordinateElevations.forEach((point) => {
            dist += Number(point.deltaDistance) || 0;
            if (point.elevation !== null && point.elevation !== undefined) {
                pts.push({dist, ele: point.elevation});
            }
        });

        if (pts.length < 2) {
            return [];
        }

        // A climb is a sustained ascent; a drop of more than DESCENT_TOL below the running peak ends it.
        const MIN_GAIN = 30;      // m
        const MIN_LENGTH = 500;   // m
        const MIN_AVG_GRADE = 3;  // %
        const DESCENT_TOL = 25;   // m

        const climbs = [];
        let lowIdx = 0;
        let peakIdx = 0;

        const closeCandidate = (endIdx) => {
            const length = pts[peakIdx].dist - pts[lowIdx].dist;
            const gain = pts[peakIdx].ele - pts[lowIdx].ele;
            const avgGrade = length > 0 ? (gain / length) * 100 : 0;

            if (gain >= MIN_GAIN && length >= MIN_LENGTH && avgGrade >= MIN_AVG_GRADE) {
                let maxGrade = 0;
                for (let k = lowIdx + 1; k <= peakIdx; k++) {
                    const segLength = pts[k].dist - pts[k - 1].dist;
                    if (segLength > 0) {
                        maxGrade = Math.max(maxGrade, (pts[k].ele - pts[k - 1].ele) / segLength * 100);
                    }
                }

                climbs.push({
                    startDistance: Math.round(pts[lowIdx].dist),
                    endDistance: Math.round(pts[peakIdx].dist),
                    length: Math.round(length),
                    gain: Math.round(gain),
                    avgGrade: Math.round(avgGrade * 10) / 10,
                    maxGrade: Math.round(maxGrade * 10) / 10,
                    category: this._climbCategory(gain, avgGrade)
                });
            }

            lowIdx = endIdx;
            peakIdx = endIdx;
        };

        for (let i = 1; i < pts.length; i++) {
            // Still searching for the bottom: track the running minimum.
            if (peakIdx === lowIdx && pts[i].ele < pts[lowIdx].ele) {
                lowIdx = i;
                peakIdx = i;
            }

            if (pts[i].ele > pts[peakIdx].ele) {
                peakIdx = i;
            }

            const droppedFromPeak = pts[peakIdx].ele - pts[i].ele;
            if ((droppedFromPeak > DESCENT_TOL || i === pts.length - 1) && peakIdx > lowIdx) {
                closeCandidate(i);
            }
        }

        return climbs;
    };

    // Technical difficulty of a hike (distinct from the IBP effort level): derived from the steepest
    // sustained grade and the share of unpaved terrain. Returns { level, maxGrade, unpavedRatio }.
    // level ∈ 'easy' | 'technical' | 'very_technical'. unpavedRatio may be null when unknown.
    estimateTechnicity = (coordinateElevations, unpavedRatio = null) => {
        if (!Array.isArray(coordinateElevations) || coordinateElevations.length < 2) {
            return null;
        }

        const rawPts = [];
        let dist = 0;
        coordinateElevations.forEach((point) => {
            dist += Number(point.deltaDistance) || 0;
            if (point.elevation !== null && point.elevation !== undefined) {
                rawPts.push({dist, ele: point.elevation});
            }
        });

        if (rawPts.length < 2) {
            return null;
        }

        // Elevation samples (DEM/API) carry several metres of vertical noise, which shows up as
        // absurd micro-grades. Measure the steepest grade as the least-squares SLOPE over a ~120 m
        // window (a line fit through every point in the window averages the noise out, far more
        // robustly than an endpoint difference).
        const WINDOW = 120;
        let maxGrade = 0;
        for (let i = 0; i < rawPts.length; i++) {
            let n = 0;
            let sumX = 0;
            let sumY = 0;
            let sumXX = 0;
            let sumXY = 0;
            let j = i;
            while (j < rawPts.length && rawPts[j].dist - rawPts[i].dist <= WINDOW) {
                const x = rawPts[j].dist;
                const y = rawPts[j].ele;
                n++;
                sumX += x;
                sumY += y;
                sumXX += x * x;
                sumXY += x * y;
                j++;
            }

            // Need a real span (not the sparse tail) and at least 3 points for a meaningful fit.
            if (n < 3 || rawPts[j - 1].dist - rawPts[i].dist < WINDOW * 0.5) {
                continue;
            }

            const denominator = n * sumXX - sumX * sumX;
            if (denominator !== 0) {
                const slope = (n * sumXY - sumX * sumY) / denominator;
                maxGrade = Math.max(maxGrade, Math.abs(slope) * 100);
            }
        }

        // Cap at a realistic ceiling: a grade sustained over 120 m beyond ~35 % is implausible for a
        // planned running/hiking/cycling route and almost always residual elevation-data error.
        maxGrade = Math.min(maxGrade, 35);

        let score = 0;
        if (maxGrade >= 25) {
            score += 2;
        } else if (maxGrade >= 15) {
            score += 1;
        }

        if (unpavedRatio !== null) {
            if (unpavedRatio >= 0.6) {
                score += 1;
            } else if (unpavedRatio >= 0.3) {
                score += 0.5;
            }
        }

        let level = 'easy';
        if (score >= 2.5) {
            level = 'very_technical';
        } else if (score >= 1) {
            level = 'technical';
        }

        return {level, maxGrade: Math.round(maxGrade), unpavedRatio};
    };

    // Rough climb category from gain (m) and average grade (%): score = gain × avgGrade.
    _climbCategory = (gain, avgGrade) => {
        const score = gain * avgGrade;

        if (score >= 8000) return 'hc';
        if (score >= 4000) return '1';
        if (score >= 2000) return '2';
        if (score >= 1000) return '3';
        if (score >= 400) return '4';

        return 'bump';
    };

    // Params:
    // rideType: current ride type (running, ...)
    // distance: total distance
    // elevationByDistance: array of [deltaDistance, elevation]
    estimateDuration = (rideType, distance, elevationByDistance) => {
        const averageSpeed = parseFloat(window.averageRideTypeSpeed[rideType]);

        if (ElevationRideTypes.includes(rideType)) {
            const slopeLevels = this._computeSlopeLevels(elevationByDistance);

            const totalAverageSpeed = (slopeLevels.flat * (averageSpeed * 2.5)) +
                ((slopeLevels.upward1To5 + slopeLevels.upward5To10) * (averageSpeed * 1.75)) +
                (slopeLevels.upward10To15 * averageSpeed) +
                (slopeLevels.upward15To30 * (averageSpeed * 0.45)) +
                (slopeLevels.upward30To55 * (averageSpeed * 0.3)) +
                ((slopeLevels.downward1To5 + slopeLevels.downward5To10) * (averageSpeed * 3)) +
                (slopeLevels.downward10To15 * (averageSpeed * 2)) +
                (slopeLevels.downward15To30 * averageSpeed) +
                (slopeLevels.downward30To55 * averageSpeed * 0.75);

            return (distance / 1000) / totalAverageSpeed * 3600;
        } else {
            return (distance || 0) * 3.6 / averageSpeed;
        }
    };

    // Params:
    //   rideType: running | hiking | bike | walk
    //   distance: total distance in meters
    //   elevationByDistance: array of [deltaDistance (m), elevation (m)]
    //   paceProfile: tourist | standard | sport (defaults to standard)
    estimateServerDuration = (rideType, distance, elevationByDistance, {paceProfile = 'standard'} = {}) => {
        const paceFactor = PaceProfileFactors[paceProfile] ?? 1.0;
        const baseSpeed = parseFloat(window.averageRideTypeSpeed?.[rideType]) || AverageSpeedByRideType[rideType];

        if (!Number.isFinite(baseSpeed) || baseSpeed <= 0) {
            return 0;
        }

        const speed = baseSpeed * paceFactor;
        let durationInSeconds;

        if (rideType === 'hiking') {
            const profile = Array.isArray(elevationByDistance) ? elevationByDistance : [];
            const smoothed = this._smoothElevationByDistance(profile, rideType);
            const {distances: d} = this._computeSlopeDistances(smoothed);

            durationInSeconds = (
                (d.flat / 1000 / (speed * 2.5)) +
                ((d.upward1To5 + d.upward5To10) / 1000 / (speed * 1.75)) +
                (d.upward10To15 / 1000 / (speed * 1.25)) +
                (d.upward15To30 / 1000 / (speed * 0.75)) +
                (d.upward30To55 / 1000 / (speed * 0.5)) +
                ((d.downward1To5 + d.downward5To10) / 1000 / (speed * 3)) +
                (d.downward10To15 / 1000 / (speed * 2.5)) +
                (d.downward15To30 / 1000 / (speed * 1.75)) +
                (d.downward30To55 / 1000 / speed)
            ) * 3600;
        } else {
            // Flat distance / speed, plus a lighter ascent-only penalty for bike & walk (extra seconds
            // per metre climbed, scaled down at faster pace). Running and any other type stay flat.
            durationInSeconds = (distance || 0) * 3.6 / speed;

            const ascentPerMeter = AscentTimeSecondsPerMeter[rideType];
            if (ascentPerMeter && elevationByDistance?.length >= 2) {
                const gain = this._positiveElevationGain(this._smoothElevationByDistance(elevationByDistance, rideType));
                durationInSeconds += gain * ascentPerMeter / paceFactor;
            }
        }

        if (!Number.isFinite(durationInSeconds)) {
            return 0;
        }

        return Math.round(durationInSeconds / 60) * 60;
    };

    // Total positive elevation gain (metres) over the profile — sum of the uphill deltas. Input:
    // [deltaDistance, elevation] pairs (smooth first). Feeds the bike/walk ascent penalty.
    _positiveElevationGain = (elevationByDistance) => {
        let gain = 0;
        let previousElevation = null;

        for (let i = 0; i < elevationByDistance.length; i++) {
            const elevation = elevationByDistance[i][1];
            if (previousElevation !== null && Number.isFinite(elevation)) {
                const delta = elevation - previousElevation;
                if (delta > 0) {
                    gain += delta;
                }
            }
            if (Number.isFinite(elevation)) {
                previousElevation = elevation;
            }
        }

        return gain;
    };

    // MET values per ride type (Metabolic Equivalent of Task)
    MET_VALUES = {
        running: 9.8,
        hiking: 6.0,
        bike: 7.5,
        walk: 3.5
    };

    DEFAULT_WEIGHT_KG = 70;

    estimateCalories = (rideType, durationInSeconds, distanceInMeters, positiveElevation = 0, weightKg = null) => {
        if (!durationInSeconds || !distanceInMeters) return 0;

        const weight = weightKg || this.DEFAULT_WEIGHT_KG;
        let met = this.MET_VALUES[rideType] || this.MET_VALUES.walk;

        if (ElevationRideTypes.includes(rideType) && distanceInMeters > 0 && positiveElevation > 0) {
            const avgUphillGrade = (positiveElevation / (distanceInMeters / 1000)) / 10;
            met += avgUphillGrade * 0.1;
        }

        const durationInHours = durationInSeconds / 3600;

        return Math.round(met * weight * durationInHours);
    };

    formatCalories = (calories) => {
        if (!calories) return '-';
        return `${calories} kcal`;
    };

    // returns difficulty according to ride type:
    // By default: 0 (level undefined)
    // 1 (very easy), 2 (easy), 3 (average), 4 (difficult), 5 (very difficult)
    estimateLevel = (rideType, distance, elevationByDistance, slopeLevels = undefined) => {
        slopeLevels ||= this._computeSlopeLevels(elevationByDistance);

        const ibpLevel = this._computeIBPDifficulty(distance, slopeLevels);
        let difficultyLevel = 0;

        if (ElevationRideTypes.includes(rideType)) {
            if (ibpLevel >= 0 && ibpLevel < 25) {
                difficultyLevel = 1;
            } else if (ibpLevel >= 25 && ibpLevel < 35) {
                difficultyLevel = 2;
            } else if (ibpLevel >= 35 && ibpLevel < 55) {
                difficultyLevel = 3;
            } else if (ibpLevel >= 55 && ibpLevel < 75) {
                difficultyLevel = 4;
            } else {
                difficultyLevel = 5;
            }
        } else if (rideType === 'running') {
            if (distance < 5_000) {
                difficultyLevel = 1;
            } else if (distance >= 5_000 && distance < 8_000) {
                difficultyLevel = 2;
            } else if (distance >= 8_000 && distance < 12_000) {
                difficultyLevel = 3;
            } else if (distance >= 12_000 && distance < 18_000) {
                difficultyLevel = 4;
            } else {
                difficultyLevel = 5;
            }
        }
        if (rideType === 'walk') {
            if (distance < 3_000) {
                difficultyLevel = 1;
            } else if (distance >= 3_000 && distance < 5_000) {
                difficultyLevel = 2;
            } else if (distance >= 5_000 && distance < 8_000) {
                difficultyLevel = 3;
            } else if (distance >= 8_000 && distance < 12_000) {
                difficultyLevel = 4;
            } else {
                difficultyLevel = 5;
            }
        }

        return difficultyLevel;
    };

    /* Private Methods
    ******************** */
    _secondsToHMS = (durationInSeconds) => {
        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor((durationInSeconds - (hours * 3600)) / 60);
        const seconds = Math.floor(durationInSeconds - (hours * 3600) - (minutes * 60));

        return [hours, minutes, seconds];
    };

    _displayFormattedTime = (hours, minutes, seconds, withLeadingZero = false) => {
        if (hours && hours < 10) {
            hours = withLeadingZero ? '0' + hours : hours;
        } else if (!hours) {
            hours = 0;
        }
        if (minutes && minutes < 10) {
            minutes = withLeadingZero ? '0' + minutes : minutes;
        } else if (!minutes) {
            minutes = 0;
        }
        if (seconds && seconds < 10) {
            seconds = withLeadingZero ? '0' + seconds : seconds;
        } else if (!seconds) {
            seconds = 0;
        }

        const isImperial = window.unit === 'mi';
        const hLabel = isImperial ? 'hr' : 'h';
        const mLabel = isImperial ? 'min' : 'm';
        const sLabel = isImperial ? 'sec' : 's';
        const mLong = 'min';
        const sLong = 'sec';

        if (hours) {
            if (seconds) {
                return `${hours}${hLabel} ${minutes}${mLabel} ${seconds}${sLabel}`;
            } else if (minutes) {
                return `${hours}${hLabel} ${minutes}${mLabel}`;
            } else {
                return `${hours}${hLabel}`;
            }
        } else if (minutes) {
            if (seconds) {
                return `${minutes}${mLong} ${seconds}${sLong}`;
            } else {
                return `${minutes}${mLong}`;
            }
        } else {
            return `${seconds}${sLong}`;
        }
    };

    _formatToChrono = (hours, minutes, seconds) => {
        return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    // Smooth an elevation profile over a distance window (centered moving average). Raw terrain-DEM
    // samples (~1 pt / 30-40 m, zoom-limited) are noisy and create fabricated steep short segments; the
    // server LOWESS-smooths elevation before binning slopes, spreading each climb over a longer distance.
    // This approximates that so a local duration estimate matches the one the server computes on save.
    // The window is a fixed distance per ride type (ElevationSmoothingSpanByRideType, scaled by
    // ElevationSmoothingWindowFactor), NOT a fraction of the route: the scale at which a DEM stops
    // resolving relief belongs to the data, not to how long the outing is — a window that grew with the
    // route flattened the real climbs of the long ones. Input/output: [deltaDistance, elevation].
    _smoothElevationByDistance = (elevationByDistance, rideType) => {
        const n = elevationByDistance.length;
        if (n < 3) {
            return elevationByDistance;
        }

        const cumulative = new Array(n);
        let acc = 0;
        for (let i = 0; i < n; i++) {
            acc += elevationByDistance[i][0] || 0;
            cumulative[i] = acc;
        }

        const span = ElevationSmoothingSpanByRideType[rideType] ?? DefaultElevationSmoothingSpan;
        const windowMeters = span * ElevationSmoothingWindowFactor;
        const half = windowMeters / 2;
        const smoothed = new Array(n);
        let lo = 0;
        let hi = 0;

        for (let i = 0; i < n; i++) {
            const center = cumulative[i];

            while (lo < n && cumulative[lo] < center - half) {
                lo++;
            }
            while (hi < n && cumulative[hi] <= center + half) {
                hi++;
            }

            let sum = 0;
            let count = 0;
            for (let j = lo; j < hi; j++) {
                const elevation = elevationByDistance[j][1];
                if (Number.isFinite(elevation)) {
                    sum += elevation;
                    count++;
                }
            }

            smoothed[i] = [elevationByDistance[i][0], count ? sum / count : elevationByDistance[i][1]];
        }

        return smoothed;
    };

    _computeSlopeDistances = (elevationByDistance) => {
        const distances = {
            upward30To55: 0,
            upward15To30: 0,
            upward10To15: 0,
            upward5To10: 0,
            upward1To5: 0,
            flat: 0,
            downward1To5: 0,
            downward5To10: 0,
            downward10To15: 0,
            downward15To30: 0,
            downward30To55: 0
        };
        let totalDistance = 0;
        let previousElevation = null;

        for (let i = 0; i < elevationByDistance.length; i++) {
            const [deltaDistance, elevation] = elevationByDistance[i];

            totalDistance += deltaDistance;

            if (previousElevation !== null && deltaDistance) {
                const currentSlope = (elevation - previousElevation) / deltaDistance * 100;

                if (currentSlope >= -1 && currentSlope <= 1) {
                    distances.flat += deltaDistance;
                } else if (currentSlope > 1 && currentSlope <= 5) {
                    distances.upward1To5 += deltaDistance;
                } else if (currentSlope > 5 && currentSlope <= 10) {
                    distances.upward5To10 += deltaDistance;
                } else if (currentSlope > 10 && currentSlope <= 15) {
                    distances.upward10To15 += deltaDistance;
                } else if (currentSlope > 15 && currentSlope <= 30) {
                    distances.upward15To30 += deltaDistance;
                } else if (currentSlope > 30) {
                    distances.upward30To55 += deltaDistance;
                } else if (currentSlope < -1 && currentSlope >= -5) {
                    distances.downward1To5 += deltaDistance;
                } else if (currentSlope < -5 && currentSlope >= -10) {
                    distances.downward5To10 += deltaDistance;
                } else if (currentSlope < -10 && currentSlope >= -15) {
                    distances.downward10To15 += deltaDistance;
                } else if (currentSlope < -15 && currentSlope >= -30) {
                    distances.downward15To30 += deltaDistance;
                } else if (currentSlope < -30) {
                    distances.downward30To55 += deltaDistance;
                }
            }

            previousElevation = elevation;
        }

        return {distances, totalDistance};
    };

    _computeSlopeLevels = (elevationByDistance) => {
        let totalDistance = 0;
        const slopeDistance = {
            upward30To55: 0,
            upward15To30: 0,
            upward10To15: 0,
            upward5To10: 0,
            upward1To5: 0,
            flat: 0,
            downward1To5: 0,
            downward5To10: 0,
            downward10To15: 0,
            downward15To30: 0,
            downward30To55: 0
        };
        let previousElevation = null;
        let currentSlope;

        for (let i = 0; i < elevationByDistance.length; i++) {
            const [deltaDistance, elevation] = elevationByDistance[i];

            totalDistance += deltaDistance;

            if (previousElevation) {
                currentSlope = (elevation - previousElevation) / deltaDistance * 100;

                if (currentSlope >= -1 && currentSlope <= 1) {
                    slopeDistance.flat += deltaDistance;
                } else if (currentSlope > 1 && currentSlope <= 5) {
                    slopeDistance.upward1To5 += deltaDistance;
                } else if (currentSlope > 5 && currentSlope <= 10) {
                    slopeDistance.upward5To10 += deltaDistance;
                } else if (currentSlope > 10 && currentSlope <= 15) {
                    slopeDistance.upward10To15 += deltaDistance;
                } else if (currentSlope > 15 && currentSlope <= 30) {
                    slopeDistance.upward15To30 += deltaDistance;
                } else if (currentSlope > 30) {
                    slopeDistance.upward30To55 += deltaDistance;
                } else if (currentSlope < -1 && currentSlope >= -5) {
                    slopeDistance.downward1To5 += deltaDistance;
                } else if (currentSlope < -5 && currentSlope >= -10) {
                    slopeDistance.downward5To10 += deltaDistance;
                } else if (currentSlope < -10 && currentSlope >= -15) {
                    slopeDistance.downward10To15 += deltaDistance;
                } else if (currentSlope < -15 && currentSlope >= -30) {
                    slopeDistance.downward15To30 += deltaDistance;
                } else if (currentSlope < -30) {
                    slopeDistance.downward30To55 += deltaDistance;
                }
            }

            previousElevation = elevation;
        }

        let slopePercent = {};
        if (totalDistance && totalDistance !== 0) {
            slopePercent = {
                upward30To55: slopeDistance.upward30To55 / totalDistance,
                upward15To30: slopeDistance.upward15To30 / totalDistance,
                upward10To15: slopeDistance.upward10To15 / totalDistance,
                upward5To10: slopeDistance.upward5To10 / totalDistance,
                upward1To5: slopeDistance.upward1To5 / totalDistance,
                flat: slopeDistance.flat / totalDistance,
                downward1To5: slopeDistance.downward1To5 / totalDistance,
                downward5To10: slopeDistance.downward5To10 / totalDistance,
                downward10To15: slopeDistance.downward10To15 / totalDistance,
                downward15To30: slopeDistance.downward15To30 / totalDistance,
                downward30To55: slopeDistance.downward30To55 / totalDistance
            };
        }

        return slopePercent;
    };

    _computeIBPDifficulty = (distance, slopeLevels) => {
        const difficultyIndex = (slopeLevels.flat * 0.5) +
            ((slopeLevels.upward1To5 + slopeLevels.downward1To5) * 1) +
            ((slopeLevels.upward5To10 + slopeLevels.downward5To10) * 2) +
            ((slopeLevels.upward10To15 + slopeLevels.downward10To15) * 3) +
            ((slopeLevels.upward15To30 + slopeLevels.downward15To30) * 4) +
            ((slopeLevels.upward30To55 + slopeLevels.downward30To55) * 5);

        const distanceIndex = distance / (1_000 * Math.log((distance / 1_000) + 1)) * 8;

        return parseInt(difficultyIndex * distanceIndex);
    };
}

export default new Computation();
